import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export interface ModalProps {
  isOpen?: boolean | undefined;
  onClose?: (() => void) | undefined;
  title?: React.ReactNode | undefined;
  description?: React.ReactNode | undefined;
  children?: React.ReactNode | undefined;
  footer?: React.ReactNode | undefined;
  size?: 'sm' | 'md' | 'lg' | 'xl' | undefined;
  className?: string | undefined;
}

export function Modal({
  isOpen = true,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className = '',
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Focus trap & Escape key listener
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }

      if (e.key === 'Tab' && panelRef.current) {
        const focusableElements = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (lastElement && document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (firstElement && document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Initial focus focusable element or panel
    const timer = setTimeout(() => {
      if (panelRef.current) {
        const firstInput = panelRef.current.querySelector<HTMLElement>(
          'input, button, select, textarea',
        );
        if (firstInput) {
          firstInput.focus();
        } else {
          panelRef.current.focus();
        }
      }
    }, 50);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timer);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
  }[size];

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center nav-blur bg-slate-950/60 p-4 animate-log-slide"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`w-full ${sizeClasses} rounded-xl2 bg-card border border-line p-6 shadow-cardhover outline-none ${className}`}
      >
        {(title || description || onClose) && (
          <div className="flex items-start justify-between pb-4 border-b border-line mb-4">
            <div>
              {title && (
                <h3 id="modal-title" className="text-lg font-bold text-ink">
                  {title}
                </h3>
              )}
              {description && (
                <p className="mt-1 text-sm text-sub">{description}</p>
              )}
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1 text-sub hover:text-ink hover:bg-slate-500/10 transition-colors"
                aria-label="Close dialog"
              >
                ✕
              </button>
            )}
          </div>
        )}

        {children && <div className="space-y-4">{children}</div>}

        {footer && (
          <div className="mt-6 pt-4 border-t border-line flex items-center justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function ModalHeader({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={`pb-3 border-b border-line mb-4 ${className}`} {...props}>{children}</div>;
}

export function ModalTitle({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={`text-lg font-bold text-ink ${className}`} {...props}>{children}</h3>;
}

export function ModalBody({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={`space-y-4 ${className}`} {...props}>{children}</div>;
}

export function ModalFooter({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`mt-6 pt-4 border-t border-line flex items-center justify-end gap-3 ${className}`} {...props}>
      {children}
    </div>
  );
}

// Imperative Modal API Context for backwards compatibility
export interface ModalOptions {
  title: string;
  description?: string | undefined;
  confirmText?: string | undefined;
  cancelText?: string | undefined;
  onConfirm?: (() => void | Promise<void>) | undefined;
  onCancel?: (() => void) | undefined;
  variant?: 'default' | 'destructive' | undefined;
}

const ModalContext = createContext<{
  openModal: (options: ModalOptions) => void;
  closeModal: () => void;
} | null>(null);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [modalOptions, setModalOptions] = useState<ModalOptions | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const openModal = useCallback((options: ModalOptions) => {
    setModalOptions(options);
  }, []);

  const closeModal = useCallback(() => {
    setModalOptions(null);
    setIsLoading(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!modalOptions?.onConfirm) {
      closeModal();
      return;
    }
    setIsLoading(true);
    try {
      await modalOptions.onConfirm();
      closeModal();
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  }, [modalOptions, closeModal]);

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {modalOptions && (
        <Modal
          isOpen={true}
          onClose={() => {
            modalOptions.onCancel?.();
            closeModal();
          }}
          title={modalOptions.title}
          description={modalOptions.description}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  modalOptions.onCancel?.();
                  closeModal();
                }}
                className="btn-g text-xs px-4 py-2"
                disabled={isLoading}
              >
                {modalOptions.cancelText ?? 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isLoading}
                className={`btn-p text-xs px-4 py-2 ${
                  modalOptions.variant === 'destructive'
                    ? 'bg-red-600 hover:bg-red-500 shadow-none'
                    : ''
                }`}
              >
                {isLoading ? (
                  <span className="animate-spin text-white">🌀</span>
                ) : null}
                {modalOptions.confirmText ?? 'Confirm'}
              </button>
            </>
          }
        />
      )}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
